import { useState, useEffect, useRef } from "react";
import ConnectedAccounts from "./ConnectedAccounts";
import { RIcon } from "./RibbonIcons";
import { WORKSPACE_THEME } from "./workspaceTheme";
import { useModalA11y } from "./useModalA11y";

// Same values as before — now sourced from the shared workspaceTheme.js
// instead of a copy hard-coded in this file (see that file's header
// comment). Kept the local name `C` so every existing usage below is
// unchanged.
const C = WORKSPACE_THEME;


function Btn({children,onClick,primary,danger,disabled,sm}){
  const [h,setH]=useState(false);
  let bg=h?"#f3f2f1":"#fff", color=C.text, border="1px solid "+C.border;
  if(primary){bg=h?C.navDark:C.nav;color="#fff";border="none";}
  if(danger){bg=h?"#fde7e9":"#fff";color=C.red;border="1px solid "+C.red+"44";}
  return(
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{background:bg,color,border,borderRadius:3,padding:sm?"4px 10px":"6px 16px",fontSize:sm?11:12,cursor:disabled?"not-allowed":"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:500,opacity:disabled?.5:1,display:"inline-flex",alignItems:"center",gap:5,whiteSpace:"nowrap",transition:"background .1s"}}>
      {children}
    </button>
  );
}

function TBtn({icon,label,active,onClick}){
  const [h,setH]=useState(false);
  return(
    <button onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{background:active?C.accentL:h?"#f3f2f1":"transparent",border:"1px solid "+(active?C.accent:"transparent"),borderRadius:3,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",color:active?C.accent:C.text,display:"inline-flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
      <RIcon name={icon} size={13} color={active?C.accent:C.textSub}/>{label}
    </button>
  );
}

//  Account Settings Dialog 
function AccountSettingsDialog({accounts,onClose,onAdd,onRemove,onSetDefault,onEdit,addToast,exportAddressBook}){
  const [tab,setTab]=useState("email");
  const [sel,setSel]=useState(null);
  const [editMode,setEditMode]=useState(false);
  const [editForm,setEditForm]=useState(null);
  const dialogRef=useRef(null);
  useModalA11y(true,onClose,dialogRef);

  const tabs=[
    {id:"email",label:"Email"},
    {id:"datafiles",label:"Data Files"},
    {id:"rss",label:"RSS Feeds"},
    {id:"calendars",label:"Internet Calendars"},
    {id:"published",label:"Published Calendars"},
    {id:"addressbooks",label:"Address Books"},
  ];

  const selAcc=accounts.find(a=>a.id===sel);

  return(
    <>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div ref={dialogRef} tabIndex={-1} role="dialog" aria-label="Account Settings" style={{background:C.white,width:700,maxHeight:"85vh",borderRadius:4,boxShadow:"0 8px 32px rgba(0,0,0,.25)",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif",overflow:"hidden",outline:"none"}}>
          {/* Title */}
          <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#f8f7f6"}}>
            <p style={{fontSize:14,fontWeight:600,color:C.text}}>Account Settings</p>
            <button onClick={onClose} aria-label="Close" style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,lineHeight:1,display:"flex"}}><RIcon name="close" size={14}/></button>
          </div>
          {/* Tabs */}
          <div style={{display:"flex",borderBottom:"1px solid "+C.border,padding:"0 12px",background:"#f8f7f6",overflowX:"auto"}}>
            {tabs.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)}
                style={{padding:"7px 14px",border:"none",borderBottom:"2px solid "+(tab===t.id?C.accent:"transparent"),background:"transparent",color:tab===t.id?C.accent:C.text,fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:tab===t.id?600:400,whiteSpace:"nowrap",marginBottom:-1}}>
                {t.label}
              </button>
            ))}
          </div>

          {tab==="email"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
              {/* Description */}
              <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border,background:"#fff"}}>
                <p style={{fontSize:12,color:C.textSub}}>You can add or remove an account. You can select an account and change its settings.</p>
              </div>
              {/* Toolbar */}
              <div style={{padding:"6px 12px",borderBottom:"1px solid "+C.border,display:"flex",gap:4,background:"#f8f7f6",flexWrap:"wrap"}}>
                <TBtn icon="plusCircle" label="New..." onClick={onAdd}/>
                <TBtn icon="refreshDouble" label="Repair..." onClick={()=>{
                  if(!sel){addToast("Select an account first","info");return;}
                  addToast("Reconnecting "+selAcc?.email+" — sign in again below to fix a broken connection","info");
                  onAdd();
                }}/>
                <TBtn icon="doc" label="Change..." onClick={()=>{if(!sel){addToast("Select an account first","info");return;}setEditForm({displayName:selAcc?.name||selAcc?.email,syncEnabled:selAcc?.syncEnabled!==false});setEditMode(true);}}/>
                <TBtn icon="check" label="Set as Default" onClick={()=>sel&&onSetDefault(sel)} active={selAcc?.isDefault}/>
                <TBtn icon="trash" label="Remove" onClick={()=>sel&&onRemove(sel)}/>
              </div>
              {/* Account list */}
              <div style={{flex:1,overflowY:"auto"}}>
                {/* Header */}
                <div style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 100px 100px",padding:"6px 16px",background:"#f3f2f1",borderBottom:"1px solid "+C.border}}>
                  {["Name","Account Type","Status","Default","Last Sync"].map(h=>(
                    <span key={h} style={{fontSize:11,fontWeight:600,color:C.textSub}}>{h}</span>
                  ))}
                </div>
                {accounts.map(acc=>(
                  <div key={acc.id} onClick={()=>setSel(acc.id)}
                    style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 100px 100px",padding:"8px 16px",borderBottom:"1px solid "+C.borderLight,background:sel===acc.id?C.sel:"#fff",cursor:"pointer"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {acc.isDefault&&<RIcon name="check" size={13} color={C.green}/>}
                      <span style={{fontSize:12,fontWeight:acc.isDefault?600:400,color:C.text}}>{acc.email}</span>
                    </div>
                    <span style={{fontSize:11,color:C.textSub}}>{acc.type}</span>
                    <span style={{fontSize:11}}>
                      <span style={{background:acc.status==="Active"?C.greenL:C.redL,color:acc.status==="Active"?C.green:C.red,padding:"1px 7px",borderRadius:3,fontWeight:500}}>{acc.status}</span>
                    </span>
                    <span style={{fontSize:11,color:acc.isDefault?C.green:C.textSub}}>{acc.isDefault?"Default":""}</span>
                    <span style={{fontSize:10,color:C.textMuted}}>{acc.lastSyncAt?new Date(acc.lastSyncAt).toLocaleDateString():"Not yet"}</span>
                  </div>
                ))}
                {accounts.length===0&&(
                  <div style={{padding:"32px 16px",textAlign:"center",color:C.textSub,fontSize:13}}>
                    No accounts configured. Click <strong>New...</strong> to add an account.
                  </div>
                )}
              </div>
              {/* Footer info */}
              {selAcc&&(
                <div style={{padding:"10px 16px",borderTop:"1px solid "+C.border,background:"#f8f7f6"}}>
                  <p style={{fontSize:12,color:C.textSub,marginBottom:3}}>Selected account delivers new messages to:</p>
                  <p style={{fontSize:12,fontWeight:600,color:C.text}}>{selAcc.email} — Inbox</p>
                  <p style={{fontSize:11,color:C.textMuted}}>{selAcc.imapConfigured?"Synced into Workspace's own database, not stored as a local file":"Send-only — no IMAP configured, so incoming mail isn't synced here"}</p>
                </div>
              )}
            </div>
          )}

          {tab==="datafiles"&&(
            <div style={{flex:1,padding:20,overflowY:"auto"}}>
              <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:8}}>Data Files</p>
              <div style={{background:"#f8f7f6",border:"1px solid "+C.border,borderRadius:4,padding:16}}>
                <p style={{fontSize:12,color:C.textSub,lineHeight:1.6}}>
                  Data files (.pst/.ost) are a Windows-desktop-Outlook concept for storing offline local copies of a mailbox.
                  This is a cloud app — your email lives in your real mailbox and in Workspace's own database, with nothing
                  stored as a local file, so there's nothing to create, import, or export here.
                </p>
              </div>
            </div>
          )}

          {tab==="addressbooks"&&(
            <div style={{flex:1,padding:20,overflowY:"auto"}}>
              <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:8}}>Address Books</p>
              <p style={{fontSize:12,color:C.textSub,marginBottom:14}}>
                This app has one real contact directory — your Contacts list, shared across Mail Merge, Sequences, and Campaigns.
                "Global Address List" and separate synced directories are an Exchange-server concept that doesn't apply here.
              </p>
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                <Btn sm primary onClick={exportAddressBook}><RIcon name="download" size={12} color="#fff"/> Export Contacts as CSV</Btn>
              </div>
              <div style={{border:"1px solid "+C.border,borderRadius:3,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",padding:"6px 12px",background:"#f3f2f1",borderBottom:"1px solid "+C.border}}>
                  {["Address Book","Type","Status"].map(h=><span key={h} style={{fontSize:11,fontWeight:600,color:C.textSub}}>{h}</span>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",padding:"8px 12px"}}>
                  <span style={{fontSize:12,color:C.text}}>Contacts</span>
                  <span style={{fontSize:11,color:C.textSub}}>Live</span>
                  <span style={{fontSize:11,color:C.green}}>Always current</span>
                </div>
              </div>
            </div>
          )}

          {(tab==="rss"||tab==="calendars"||tab==="published")&&(
            <div style={{flex:1,padding:32,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:C.textSub}}>
              <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>{tab==="rss"?"RSS Feeds":tab==="calendars"?"Internet Calendars":"Published Calendars"}</p>
              <p style={{fontSize:12,color:C.textSub,textAlign:"center",maxWidth:320}}>
                This isn't a feature this app has built yet{tab==="calendars"?" — real calendar sync exists in Workspace's own Calendar view, but subscribing to external internet calendars specifically isn't supported":""}.
              </p>
            </div>
          )}

          {/* Edit Account */}
          {editMode&&editForm&&(
            <div style={{position:"absolute",inset:0,background:C.white,zIndex:10,display:"flex",flexDirection:"column"}}>
              <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#f8f7f6"}}>
                <p style={{fontSize:14,fontWeight:600,color:C.text,display:"flex",alignItems:"center",gap:8}}><RIcon name="doc" size={14} color={C.textSub}/>Edit Account — {selAcc?.email}</p>
                <button onClick={()=>setEditMode(false)} style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,display:"flex"}}><RIcon name="close" size={14}/></button>
              </div>
              <div style={{flex:1,padding:20,overflowY:"auto"}}>
                <p style={{fontSize:11,color:C.textSub,marginBottom:16,lineHeight:1.5}}>
                  The server address and sign-in this account uses can't be changed here — if the connection itself is broken, use <strong>Repair...</strong> to reconnect it instead. This just controls how it's labeled and whether it syncs.
                </p>
                <div style={{marginBottom:16}}>
                  <p style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:4}}>Display Name</p>
                  <input value={editForm.displayName||""} onChange={e=>setEditForm(p=>({...p,displayName:e.target.value}))}
                    style={{width:"100%",border:"1px solid "+C.border,borderRadius:3,padding:"7px 10px",fontSize:12,color:C.text,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:C.text}}>
                  <input type="checkbox" checked={editForm.syncEnabled!==false} onChange={e=>setEditForm(p=>({...p,syncEnabled:e.target.checked}))} style={{width:14,height:14}}/>
                  Sync this account's inbox automatically
                </label>
              </div>
              <div style={{padding:"10px 16px",borderTop:"1px solid "+C.border,display:"flex",justifyContent:"flex-end",gap:8,background:"#f8f7f6"}}>
                <Btn onClick={()=>setEditMode(false)}>Cancel</Btn>
                <Btn primary onClick={async()=>{await onEdit(sel,editForm);setEditMode(false);}}>Save Changes</Btn>
              </div>
            </div>
          )}

          {/* Dialog footer */}
          <div style={{padding:"10px 16px",borderTop:"1px solid "+C.border,display:"flex",justifyContent:"flex-end",background:"#f8f7f6"}}>
            <Btn onClick={onClose}>Close</Btn>
          </div>
        </div>
      </div>
    </>
  );
}

//  File Menu
export default function FileMenu({onClose,settings,addToast,onLogout,onOpenSettings}){
  const [subMenu,setSubMenu]=useState(null); // null | "accountSettings"
  const [showDialog,setShowDialog]=useState(false);
  const [accounts,setAccounts]=useState([]);
  const [defaultAccIdx,setDefaultAccIdx]=useState(0);
  const [autoReplyOn,setAutoReplyOn]=useState(false);
  const [autoReplyText,setAutoReplyText]=useState("I am out of the office. I will respond as soon as possible.");
  const [autoReplyLoading,setAutoReplyLoading]=useState(false);
  const [autoReplySaving,setAutoReplySaving]=useState(false);
  const [storageStats,setStorageStats]=useState(null);
  const [activeSection,setActiveSection]=useState("info");
  const token=()=>localStorage.getItem("sdr_token")||"";
  const panelRef=useRef(null);
  useModalA11y(true,onClose,panelRef); // File backstage is only ever mounted while open, so "active" is always true here
  const addAccountRef=useRef(null);

  const loadAccounts=()=>{
    fetch("/api/smtp/accounts",{headers:{Authorization:"Bearer "+token()}})
      .then(r=>r.json())
      .then(list=>{
        if(!Array.isArray(list))return;
        const mapped=list.map(a=>({...a,displayName:a.name||a.email,type:a.provider==="microsoft"?"Microsoft Exchange":a.provider==="google"?"Gmail":"IMAP/SMTP",status:a.lastSyncError?"Sync error":"Active"}));
        setAccounts(mapped);
        // Reflect whichever account is actually marked default on the server,
        // instead of always assuming index 0 — otherwise the selector here
        // silently disagrees with what "Set as Default" last saved.
        const savedDefaultIdx=mapped.findIndex(a=>a.isDefault);
        if(savedDefaultIdx!==-1)setDefaultAccIdx(savedDefaultIdx);
      })
      .catch(()=>{});
  };
  useEffect(()=>{loadAccounts();},[]);

  const defaultAcc=accounts[defaultAccIdx]||accounts[0];

  useEffect(()=>{
    if(!defaultAcc?.email)return;
    setAutoReplyLoading(true);
    fetch("/api/accounts/"+encodeURIComponent(defaultAcc.email)+"/autoreply",{headers:{Authorization:"Bearer "+token()}})
      .then(r=>r.json())
      .then(d=>{if(!d.error){setAutoReplyOn(!!d.enabled);if(d.message)setAutoReplyText(d.message);}})
      .catch(()=>{})
      .finally(()=>setAutoReplyLoading(false));
    fetch("/api/accounts/"+encodeURIComponent(defaultAcc.email)+"/storage-stats",{headers:{Authorization:"Bearer "+token()}})
      .then(r=>r.json()).then(d=>{if(!d.error)setStorageStats(d);}).catch(()=>{});
  },[defaultAcc?.email]);

  const saveAutoReply=async()=>{
    if(!defaultAcc?.email)return;
    setAutoReplySaving(true);
    try{
      const r=await fetch("/api/accounts/"+encodeURIComponent(defaultAcc.email)+"/autoreply",{
        method:"PUT",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},
        body:JSON.stringify({enabled:autoReplyOn,message:autoReplyText}),
      });
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(autoReplyOn?"Automatic replies turned on":"Automatic replies turned off","success");
    }catch(e){addToast("Couldn't save: "+e.message,"error");}
    setAutoReplySaving(false);
  };

  const removeAccount=async(id)=>{
    if(!window.confirm("Disconnect this account? You can reconnect it later from Settings."))return;
    try{
      const r=await fetch("/api/smtp/accounts/"+id,{method:"DELETE",headers:{Authorization:"Bearer "+token()}});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Account disconnected","info");
      loadAccounts();
    }catch(e){addToast("Couldn't disconnect: "+e.message,"error");}
  };

  const exportAddressBook=async()=>{
    try{
      const r=await fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+token()}});
      const contacts=await r.json();
      if(!Array.isArray(contacts)||!contacts.length){addToast("No contacts to export","info");return;}
      const rows=[["First Name","Last Name","Title","Company","Email"],...contacts.map(c=>[c.firstName,c.lastName,c.title,c.company,c.email])];
      const csv=rows.map(r=>r.map(v=>'"'+(v||"")+'"').join(",")).join("\n");
      const blob=new Blob([csv],{type:"text/csv"});
      const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download="address_book.csv";
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      addToast("Address book downloaded","success");
    }catch(e){addToast("Export failed: "+e.message,"error");}
  };

  const [showAddAccountModal,setShowAddAccountModal]=useState(false);
  useModalA11y(showAddAccountModal,()=>{setShowAddAccountModal(false);loadAccounts();},addAccountRef);

  // Persists which account is "the" default — not just a local selection —
  // so it survives closing and reopening the File menu, and matches what
  // the account list ("Default" column) actually shows.
  const setDefaultById=async(id)=>{
    const idx=accounts.findIndex(a=>a.id===id);
    if(idx===-1)return;
    setDefaultAccIdx(idx);
    try{
      const r=await fetch("/api/smtp/accounts/"+id,{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},body:JSON.stringify({isDefault:true})});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Default account updated","success");
      loadAccounts();
    }catch(e){addToast("Couldn't save default account: "+e.message,"error");}
  };
  // "Change..." only edits what the server can actually persist for an
  // account (display name, sync toggle) — see the note in the Edit Account
  // panel for why server/port fields aren't editable here.
  const editAccount=async(id,form)=>{
    if(!id)return;
    try{
      const r=await fetch("/api/smtp/accounts/"+id,{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},body:JSON.stringify({name:form.displayName,syncEnabled:form.syncEnabled})});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Account updated","success");
      loadAccounts();
    }catch(e){addToast("Couldn't save changes: "+e.message,"error");}
  };

  const navItems=[
    {id:"info",icon:"infoCircle",label:"Info"},
    {id:"open",icon:"folder",label:"Open & Export"},
    {id:"save",icon:"save",label:"Save As"},
    {id:"print",icon:"print",label:"Print"},
    {id:"account",icon:"idCard",label:"Office Account"},
    {id:"options",icon:"gear",label:"Options"},
  ];

  return(
    <>
      {showDialog&&<AccountSettingsDialog accounts={accounts} onClose={()=>setShowDialog(false)} onAdd={()=>{setShowDialog(false);setShowAddAccountModal(true);}} onRemove={removeAccount} onSetDefault={setDefaultById} onEdit={editAccount} addToast={addToast} exportAddressBook={exportAddressBook}/>}
      {showAddAccountModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div ref={addAccountRef} tabIndex={-1} role="dialog" aria-label="Add Account" style={{background:"#fff",borderRadius:6,width:600,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,.3)",outline:"none"}}>
            <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center",background:"#f8f7f6"}}>
              <p style={{fontSize:14,fontWeight:600,color:C.text}}>Add Account</p>
              <button onClick={()=>{setShowAddAccountModal(false);loadAccounts();}} aria-label="Close" style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,display:"flex"}}><RIcon name="close" size={14}/></button>
            </div>
            <div style={{padding:16}}>
              <ConnectedAccounts addToast={addToast}/>
            </div>
          </div>
        </div>
      )}

      <div style={{position:"fixed",inset:0,zIndex:9990,display:"flex",fontFamily:"'Segoe UI',sans-serif"}}>
        {/* Backdrop */}
        <div onClick={onClose} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.3)"}}/>

        {/* File menu panel */}
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-label="File" style={{position:"relative",width:680,background:C.white,display:"flex",height:"100vh",boxShadow:"4px 0 24px rgba(0,0,0,.2)",zIndex:1,outline:"none"}}>
          {/* Left nav */}
          <div style={{width:180,background:"#0078d4",display:"flex",flexDirection:"column",flexShrink:0}}>
            <div style={{padding:"16px 14px 20px",borderBottom:"1px solid rgba(255,255,255,.2)"}}>
              <button onClick={onClose} aria-label="Back" style={{background:"none",border:"none",color:"#fff",cursor:"pointer",marginBottom:8,display:"flex"}}><RIcon name="arrowLeft" size={18} color="#fff"/></button>
              <p style={{fontSize:14,fontWeight:600,color:"#fff"}}>File</p>
            </div>
            <nav style={{flex:1,padding:"8px 0"}}>
              {navItems.map(n=>(
                <button key={n.id} onClick={()=>setActiveSection(n.id)}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 14px",border:"none",background:activeSection===n.id?"rgba(255,255,255,.2)":"transparent",color:"#fff",cursor:"pointer",fontSize:13,fontFamily:"'Segoe UI',sans-serif",textAlign:"left"}}>
                  <span style={{width:20,display:"flex",justifyContent:"center"}}><RIcon name={n.icon} size={15} color="#fff"/></span>
                  {n.label}
                </button>
              ))}
            </nav>
            <div style={{padding:"12px 14px",borderTop:"1px solid rgba(255,255,255,.2)"}}>
              <button onClick={onClose} style={{background:"none",border:"none",color:"rgba(255,255,255,.8)",cursor:"pointer",fontSize:12,fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",gap:6}}>
                <RIcon name="close" size={12} color="rgba(255,255,255,.8)"/> Exit
              </button>
            </div>
          </div>

          {/* Right content */}
          <div style={{flex:1,overflowY:"auto",background:"#fff"}}>

            {/* INFO section */}
            {activeSection==="info"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Account Information</p>

                {/* Account selector */}
                <div style={{border:"1px solid "+C.border,borderRadius:3,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12,background:"#f8f7f6"}}>
                  <div style={{width:40,height:40,background:"#0078d4",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",flexShrink:0}}><RIcon name="person" size={20} color="#fff"/></div>
                  <div style={{flex:1}}>
                    <p style={{fontSize:13,fontWeight:600,color:C.text}}>{defaultAcc?.email||settings?.senderEmail||"No account"}</p>
                    <p style={{fontSize:11,color:C.textSub}}>{defaultAcc?.type||"Microsoft Exchange"}</p>
                  </div>
                  <select value={defaultAcc?.id||""} style={{border:"1px solid "+C.border,borderRadius:3,padding:"4px 8px",fontSize:12,color:C.text,background:"#fff",outline:"none"}}
                    onChange={e=>setDefaultById(e.target.value)}>
                    {accounts.map(a=><option key={a.id} value={a.id}>{a.email}</option>)}
                  </select>
                </div>

                <button onClick={()=>setShowAddAccountModal(true)} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"4px 12px",fontSize:11,cursor:"pointer",marginBottom:20,fontFamily:"'Segoe UI',sans-serif",color:C.accent,display:"inline-flex",alignItems:"center",gap:5}}>
                  <RIcon name="plusCircle" size={12} color={C.accent}/> Add Account
                </button>

                {/* Account Settings card */}
                <div style={{display:"flex",gap:16,marginBottom:16,padding:"14px 0",borderTop:"1px solid "+C.border}}>
                  <div style={{width:100,display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer",flexShrink:0,position:"relative"}}
                    onClick={()=>setSubMenu(subMenu==="accountSettings"?null:"accountSettings")}>
                    <div style={{width:60,height:60,border:"1px solid "+C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",background:subMenu==="accountSettings"?C.accentL:"#f8f7f6"}}><RIcon name="idCard" size={26} color={C.accent}/></div>
                    <p style={{fontSize:11,textAlign:"center",color:C.text,fontWeight:500,lineHeight:1.3}}>Account Settings</p>
                    {subMenu==="accountSettings"&&(
                      <div style={{position:"absolute",top:"100%",left:0,width:300,background:"#fff",border:"1px solid "+C.border,borderRadius:3,boxShadow:"0 4px 16px rgba(0,0,0,.12)",zIndex:10,marginTop:4}}>
                        {[
                          {icon:"idCard",title:"Account Settings...",desc:"Add and remove accounts or change existing connection settings.",action:()=>{setShowDialog(true);setSubMenu(null);}},
                          {icon:"refresh",title:"Sync Now",desc:"Force an immediate sync for all connected accounts.",action:async()=>{setSubMenu(null);addToast("Syncing all accounts...","info");try{await fetch("/api/smtp/sync-now",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},body:"{}"});addToast("Sync complete","success");}catch(e){addToast("Sync failed: "+e.message,"error");}}},
                          {icon:"download",title:"Download Address Book",desc:"Export all your contacts as a CSV file.",action:()=>{exportAddressBook();setSubMenu(null);}},
                          {icon:"people",title:"Delegate Access",desc:"Not applicable — this platform doesn't have an Exchange-style delegate concept.",disabled:true},
                          {icon:"person",title:"Change Profile",desc:"Not applicable on this platform.",disabled:true},
                          {icon:"grid",title:"Manage Profiles",desc:"Not applicable on this platform.",disabled:true},
                        ].map((item,i)=>(
                          <button key={i} disabled={item.disabled} onClick={item.disabled?undefined:item.action}
                            style={{width:"100%",display:"flex",alignItems:"flex-start",gap:10,padding:"10px 14px",border:"none",background:"transparent",cursor:item.disabled?"default":"pointer",textAlign:"left",borderBottom:i<5?"1px solid #f0efee":"none",opacity:item.disabled?.55:1}}
                            onMouseEnter={e=>{if(!item.disabled)e.currentTarget.style.background="#f3f2f1";}} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{width:24,flexShrink:0,display:"flex",justifyContent:"center",paddingTop:2}}><RIcon name={item.icon} size={16} color={item.disabled?C.textMuted:C.accent}/></span>
                            <div>
                              <p style={{fontSize:12,fontWeight:600,color:item.disabled?C.textSub:"#0078d4",marginBottom:2,fontFamily:"'Segoe UI',sans-serif",textDecoration:item.disabled?"none":"underline"}}>{item.title}</p>
                              <p style={{fontSize:11,color:C.textSub,fontFamily:"'Segoe UI',sans-serif",lineHeight:1.4}}>{item.desc}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>Account Settings</p>
                    <p style={{fontSize:12,color:C.textSub,marginBottom:8,lineHeight:1.5}}>Change settings for this account or set up more connections.</p>
                    <ul style={{fontSize:12,color:C.textSub,paddingLeft:16,lineHeight:2}}>
                      <li><a href={defaultAcc?.provider==="microsoft"?"https://outlook.office365.com":defaultAcc?.email?.includes("@gmail.com")?"https://mail.google.com":"#"} target="_blank" rel="noreferrer" style={{color:C.accent,pointerEvents:defaultAcc?.provider!=="microsoft"&&!defaultAcc?.email?.includes("@gmail.com")?"none":"auto",opacity:defaultAcc?.provider!=="microsoft"&&!defaultAcc?.email?.includes("@gmail.com")?.5:1}}>Access this account on the web{defaultAcc?.provider!=="microsoft"&&!defaultAcc?.email?.includes("@gmail.com")?" (not available for this provider)":""}</a></li>
                      <li style={{color:C.textSub}}>No separate mobile app needed — this workspace works right in your phone's browser</li>
                    </ul>
                  </div>
                </div>

                {/* Auto Reply */}
                <div style={{display:"flex",gap:16,padding:"14px 0",borderTop:"1px solid "+C.border,marginBottom:16}}>
                  <div style={{width:100,display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer",flexShrink:0}} onClick={()=>setActiveSection("autoreply")}>
                    <div style={{width:60,height:60,border:"1px solid "+C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f7f6"}}><RIcon name="bell" size={26} color={C.amber}/></div>
                    <p style={{fontSize:11,textAlign:"center",color:C.text,fontWeight:500,lineHeight:1.3}}>Automatic Replies</p>
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>Automatic Replies (Out of Office)</p>
                    <p style={{fontSize:12,color:C.textSub,lineHeight:1.5}}>Use automatic replies to notify others when you are out of the office, on vacation, or not available to respond to email messages.</p>
                  </div>
                </div>

                {/* Mailbox Settings — informational only, no click destination, so no pointer cursor */}
                <div style={{display:"flex",gap:16,padding:"14px 0",borderTop:"1px solid "+C.border,marginBottom:16}}>
                  <div style={{width:100,display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
                    <div style={{width:60,height:60,border:"1px solid "+C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f7f6"}}><RIcon name="mail" size={26} color={C.accent}/></div>
                    <p style={{fontSize:11,textAlign:"center",color:C.text,fontWeight:500,lineHeight:1.3}}>Mailbox Settings</p>
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>Mailbox Settings</p>
                    <p style={{fontSize:12,color:C.textSub,marginBottom:8,lineHeight:1.5}}>
                      {storageStats?`${storageStats.emailCount.toLocaleString()} emails synced into Workspace, approximately ${storageStats.approxMB} MB of message content.`:"Loading..."}
                    </p>
                    <p style={{fontSize:11,color:C.textMuted||C.textSub,fontStyle:"italic"}}>
                      This reflects what's synced into Workspace, not your real provider mailbox quota — most providers (Gmail in particular) don't expose that over the connection method used here.
                    </p>
                  </div>
                </div>

                {/* Rules & Alerts — genuinely not built yet, labeled honestly rather than faking a click target */}
                <div style={{display:"flex",gap:16,padding:"14px 0",borderTop:"1px solid "+C.border,marginBottom:16}}>
                  <div style={{width:100,display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
                    <div style={{width:60,height:60,border:"1px solid "+C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f7f6",opacity:.6}}><RIcon name="filter" size={24} color={C.purple}/></div>
                    <p style={{fontSize:11,textAlign:"center",color:C.text,fontWeight:500,lineHeight:1.3}}>Manage Rules & Alerts</p>
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4,display:"flex",alignItems:"center",gap:8}}>Rules and Alerts <span style={{fontSize:10,fontWeight:600,color:C.amber,background:C.amberL,padding:"1px 8px",borderRadius:99}}>Not built yet</span></p>
                    <p style={{fontSize:12,color:C.textSub,lineHeight:1.5}}>Auto-filing rules for incoming email aren't available in this platform yet — this card is a placeholder for that, not a working feature.</p>
                  </div>
                </div>

                {/* Manage Add-ins — same: no add-in system exists here, so say so instead of pretending */}
                <div style={{display:"flex",gap:16,padding:"14px 0",borderTop:"1px solid "+C.border}}>
                  <div style={{width:100,display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
                    <div style={{width:60,height:60,border:"1px solid "+C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f7f6",opacity:.6}}><RIcon name="grid" size={24} color={C.green}/></div>
                    <p style={{fontSize:11,textAlign:"center",color:C.text,fontWeight:500,lineHeight:1.3}}>Manage Add-ins</p>
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4,display:"flex",alignItems:"center",gap:8}}>Manage Add-ins <span style={{fontSize:10,fontWeight:600,color:C.amber,background:C.amberL,padding:"1px 8px",borderRadius:99}}>Not built yet</span></p>
                    <p style={{fontSize:12,color:C.textSub,lineHeight:1.5}}>There's no add-in system in this platform yet — nothing to manage here.</p>
                  </div>
                </div>
              </div>
            )}

            {/* AUTO REPLY section */}
            {activeSection==="autoreply"&&(
              <div style={{padding:"24px 28px"}}>
                <button onClick={()=>setActiveSection("info")} style={{background:"none",border:"none",color:C.accent,fontSize:12,cursor:"pointer",marginBottom:16,fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",gap:4}}><RIcon name="arrowLeft" size={11} color={C.accent}/> Back</button>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Automatic Replies</p>
                <div style={{border:"1px solid "+C.border,borderRadius:3,padding:16,marginBottom:16}}>
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                    {[{l:"Do not send automatic replies",v:false},{l:"Send automatic replies",v:true}].map(opt=>(
                      <label key={String(opt.v)} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.text}}>
                        <input type="radio" checked={autoReplyOn===opt.v} onChange={()=>setAutoReplyOn(opt.v)} style={{width:14,height:14}}/>
                        {opt.l}
                      </label>
                    ))}
                  </div>
                  {autoReplyOn&&(
                    <div>
                      <p style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:6}}>Reply message for Inside My Organization:</p>
                      <textarea value={autoReplyText} onChange={e=>setAutoReplyText(e.target.value)} rows={4}
                        style={{width:"100%",border:"1px solid "+C.border,borderRadius:3,padding:"8px 10px",fontSize:12,color:C.text,resize:"vertical",fontFamily:"'Segoe UI',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                  )}
                </div>
                <Btn primary disabled={autoReplySaving} onClick={async()=>{await saveAutoReply();setActiveSection("info");}}>
                  {autoReplySaving?"Saving...":"Save"}
                </Btn>
              </div>
            )}

            {/* OPEN & EXPORT */}
            {activeSection==="open"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Open & Export</p>
                {[{icon:"doc",title:"Open Outlook Data File",desc:"Open another Outlook data file (.pst)",action:()=>addToast("Feature coming soon","info")},
                  {icon:"folder",title:"Open Other User's Folder",desc:"Open a folder that another person has shared with you.",action:()=>addToast("Feature coming soon","info")},
                  {icon:"upload",title:"Import/Export",desc:"Import or export files and settings, including other email programs and data files.",action:()=>addToast("Feature coming soon","info")},
                ].map((item,i)=>(
                  <div key={i} onClick={item.action} style={{display:"flex",gap:14,padding:"14px 0",borderTop:i===0?"none":"1px solid "+C.border,cursor:"pointer"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f8f7f6"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{width:32,height:32,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:C.accentL,borderRadius:6}}><RIcon name={item.icon} size={17} color={C.accent}/></span>
                    <div>
                      <p style={{fontSize:13,fontWeight:600,color:C.accent,marginBottom:3}}>{item.title}</p>
                      <p style={{fontSize:12,color:C.textSub}}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* OPTIONS */}
            {activeSection==="options"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Options</p>
                {[
                  {l:"General",sub:"SDR name, company, sender email, and other workspace-wide settings",builtIn:true,action:()=>{if(onOpenSettings)onOpenSettings();else addToast("Open Settings from the sidebar to change these","info");}},
                  {l:"Mail",builtIn:false},{l:"Calendar",builtIn:false},{l:"Contacts",builtIn:false},{l:"Advanced",builtIn:false},
                ].map(opt=>(
                  <div key={opt.l} onClick={opt.builtIn?opt.action:undefined}
                    style={{padding:"10px 14px",border:"1px solid "+C.border,borderRadius:3,marginBottom:6,cursor:opt.builtIn?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#fff",opacity:opt.builtIn?1:.6}}
                    onMouseEnter={e=>{if(opt.builtIn)e.currentTarget.style.background="#f3f2f1";}} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <div>
                      <span style={{fontSize:13,color:C.text}}>{opt.l}</span>
                      {opt.sub&&<p style={{fontSize:11,color:C.textSub,marginTop:2}}>{opt.sub}</p>}
                    </div>
                    {opt.builtIn?<RIcon name="chevronRight" size={13} color={C.textSub}/>:<span style={{fontSize:10,fontWeight:600,color:C.amber,background:C.amberL,padding:"1px 8px",borderRadius:99,whiteSpace:"nowrap"}}>Not built yet</span>}
                  </div>
                ))}
              </div>
            )}

            {/* ACCOUNT */}
            {activeSection==="account"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Office Account</p>
                <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:20,padding:16,border:"1px solid "+C.border,borderRadius:3,background:"#f8f7f6"}}>
                  <div style={{width:56,height:56,borderRadius:"50%",background:C.nav,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:22,fontWeight:700,flexShrink:0}}>
                    {(settings?.sdrName||"").trim().split(/\s+/).filter(Boolean).slice(0,2).map(w=>w[0].toUpperCase()).join("")||"?"}
                  </div>
                  <div>
                    <p style={{fontSize:14,fontWeight:600,color:C.text}}>{settings?.sdrName||"Your name"}</p>
                    <p style={{fontSize:12,color:C.textSub}}>{settings?.senderEmail||defaultAcc?.email||""}</p>
                    <button onClick={()=>{if(onLogout)onLogout();else addToast("Sign out isn't available from here","error");}}
                      style={{background:"none",border:"none",padding:0,color:C.accent,cursor:"pointer",marginTop:4,fontSize:11,fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",gap:4}}>
                      <RIcon name="arrowLeft" size={11} color={C.accent}/> Sign out
                    </button>
                  </div>
                </div>
                <p style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8}}>Product Information</p>
                <div style={{padding:12,border:"1px solid "+C.border,borderRadius:3,background:"#fff"}}>
                  <p style={{fontSize:13,fontWeight:600,color:C.text}}>SDR ReachOut Platform</p>
                  <p style={{fontSize:11,color:C.textSub}}>Version 1.0.0 · Enterprise Edition</p>
                  <p style={{fontSize:11,color:C.textSub,marginTop:4}}>© 2026 SDR ReachOut. All rights reserved.</p>
                </div>
              </div>
            )}

            {/* PRINT */}
            {activeSection==="print"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Print</p>
                <div style={{display:"flex",gap:12}}>
                  <Btn primary onClick={()=>{window.print();addToast("Printing...","info");}}><RIcon name="print" size={13} color="#fff"/> Print</Btn>
                  <Btn onClick={()=>addToast("Print preview coming soon","info")}><RIcon name="doc" size={13} color={C.text}/> Print Preview</Btn>
                </div>
              </div>
            )}

            {/* SAVE AS */}
            {activeSection==="save"&&(
              <div style={{padding:"24px 28px"}}>
                <p style={{fontSize:20,fontWeight:300,color:C.text,marginBottom:20}}>Save As</p>
                {[
                  {icon:"doc",title:"Save as PDF",builtIn:false},
                  {icon:"textLines",title:"Save as HTML",builtIn:false},
                  {icon:"download",title:"Export Contacts to CSV",builtIn:true,action:exportAddressBook},
                ].map((item,i)=>(
                  <div key={i} onClick={item.builtIn?item.action:undefined} style={{display:"flex",gap:12,padding:"12px 0",borderTop:i===0?"none":"1px solid "+C.border,cursor:item.builtIn?"pointer":"default",alignItems:"center",opacity:item.builtIn?1:.6}}
                    onMouseEnter={e=>{if(item.builtIn)e.currentTarget.style.background="#f8f7f6";}} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",background:C.accentL,borderRadius:6}}><RIcon name={item.icon} size={15} color={C.accent}/></span>
                    <span style={{fontSize:13,color:item.builtIn?C.accent:C.textSub,fontWeight:500}}>{item.title}</span>
                    {!item.builtIn&&<span style={{fontSize:10,fontWeight:600,color:C.amber,background:C.amberL,padding:"1px 8px",borderRadius:99,marginLeft:"auto"}}>Not built yet</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}